import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

/** Os campos de `EventSettings` que alguma tela escreve. */
export interface SettingsDoEvento {
  sendWindowStart: string | null;
  sendWindowEnd: string | null;
  reminderDelayHours: number;
  maxReminders: number;
  maxQuestions: number;
  aiEffort: string | null;
  aiModel: string | null;
  autoSendRecommendations: boolean;
  minScore: number;
  maxRecommendationsPerParticipant: number;
  blockSameCompany: boolean;
}

export interface ModeloDeIa {
  id: string;
  rotulo: string;
}

interface EventoComSettings {
  settings: SettingsDoEvento | null;
  modelosDeIa: ModeloDeIa[];
}

/**
 * O andaime dos blocos de parâmetros do evento.
 *
 * Os três blocos — ritmo, conversa e cruzamento — têm a mesma mecânica: carregar
 * o evento, mostrar um formulário recolhido, gravar num `PATCH`, piscar "Salvo".
 * O que muda em cada um é a rota, os campos e a permissão. Com três cópias do
 * andaime, a primeira correção de comportamento (o "Salvo" que não apagava, por
 * exemplo) teria de ser feita em três lugares e seria feita em um.
 *
 * ⚠️ **Recolhido por padrão, e isso é escolha de produto.** Parâmetro é ajuste de
 * véspera, não leitura diária: o que se quer ver ao abrir a tela de jornadas é
 * quem respondeu, e na de indicações é o que espera revisão.
 *
 * `caminho` é o sufixo da rota (`''`, `'/ia'`, `'/matchmaking'`) porque são três
 * rotas com três permissões — juntar tudo num `PATCH` deixaria quem agenda
 * disparo desligar a revisão humana das indicações.
 */
export function BlocoDeParametros({
  eventId,
  titulo,
  icone,
  caminho,
  corpo,
  children,
  rodape,
}: {
  eventId: string;
  titulo: string;
  icone: string;
  /** Sufixo de `/events/:id/settings`. */
  caminho: '' | '/ia' | '/matchmaking';
  /** Traduz o que o formulário coletou (tudo string) no corpo do PATCH. */
  corpo: (dados: Record<string, string>) => Record<string, unknown>;
  children: (evento: EventoComSettings) => ReactNode;
  rodape?: ReactNode;
}) {
  const cliente = useQueryClient();
  const [salvo, setSalvo] = useState(false);

  /**
   * A chave é compartilhada pelos três blocos de propósito.
   *
   * Todos leem o MESMO `GET /events/:id`, e gravar no bloco do cruzamento muda
   * uma linha que o bloco da conversa também mostra. Com chaves separadas, salvar
   * aqui deixaria o outro bloco exibindo o valor velho até alguém recarregar a
   * página — e os dois ficam abertos na mesma tela.
   */
  const chave = ['evento-parametros', eventId];

  const evento = useQuery({
    queryKey: chave,
    queryFn: () => api<EventoComSettings>(`/events/${eventId}`),
  });

  const salvar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api(`/events/${eventId}/settings${caminho}`, { method: 'PATCH', body: corpo(dados) }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: chave });
      setSalvo(true);
      setTimeout(() => setSalvo(false), 3000);
    },
  });

  if (!evento.data) return null;

  return (
    <details className="card" style={{ marginBottom: 18 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
        <i className={`ti ${icone}`} /> {titulo}
      </summary>

      <div style={{ marginTop: 16 }}>
        {salvar.isError && <div className="form-erro">{(salvar.error as Error).message}</div>}

        <FormularioDeParametros
          aoEnviar={(dados) => salvar.mutate(dados)}
          salvo={salvo}
          salvando={salvar.isPending}
        >
          {children(evento.data)}
        </FormularioDeParametros>

        {rodape && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 12 }}>{rodape}</div>
        )}
      </div>
    </details>
  );
}

/**
 * O formulário e o rodapé de "Salvar".
 *
 * Separado só para o `BlocoDeParametros` não ficar com dois níveis de JSX de
 * andaime em volta do que importa, que são os campos.
 */
function FormularioDeParametros({
  aoEnviar,
  salvo,
  salvando,
  children,
}: {
  aoEnviar: (dados: Record<string, string>) => void;
  salvo: boolean;
  salvando: boolean;
  children: ReactNode;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const form = new FormData(e.currentTarget);
        const dados: Record<string, string> = {};
        for (const [chave, valor] of form.entries()) {
          if (typeof valor === 'string') dados[chave] = valor.trim();
        }
        aoEnviar(dados);
      }}
    >
      {children}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          marginTop: 16,
        }}
      >
        {salvo && (
          <span style={{ color: 'var(--success)', fontSize: 13 }}>
            <i className="ti ti-check" /> Salvo
          </span>
        )}
        <button className="btn btn-primary btn-sm" type="submit" disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}

/**
 * Sim/Não como `<select>`, e não como caixa de marcar.
 *
 * ⚠️ Caixa desmarcada **não aparece** no `FormData`, então um formulário parcial
 * não tem como distinguir "a pessoa desmarcou" de "este campo não está nesta
 * tela" — e o `PATCH` é parcial. Com checkbox, desligar "não indicar a mesma
 * empresa" mandaria um corpo sem o campo, que o servidor leria como "não mexa", e
 * a tela voltaria marcada: a pessoa clicaria para sempre sem efeito.
 */
export const SIM_NAO = [
  { valor: 'sim', rotulo: 'Sim' },
  { valor: 'nao', rotulo: 'Não' },
];

export const ehSim = (v: string | undefined) => v === 'sim';
